/**
 * useAutoClearInput Hook
 *
 * Custom hook for input fields that display a default placeholder value (like "0")
 * and automatically clear when the user focuses on the field.
 *
 * This pattern is useful for:
 * - Quantity inputs (default "0")
 * - Price inputs (default "0.00")
 * - Any numeric input where 0 is the "empty" state
 * - Fields where the default value should not be manually selectable/editable
 *
 * @param {string|number} defaultValue - The default value to display (e.g., "0")
 * @param {function} onValueChange - Callback when value changes (receives new value)
 * @returns {object} Handler functions { handleFocus, handleBlur, handleChange }
 *
 * @example
 * const { handleFocus, handleBlur, handleChange } = useAutoClearInput('0', (value) => {
 *   setQuantity(value)
 * })
 *
 * <Input
 *   value={quantity}
 *   onChange={(e) => handleChange(e, quantity)}
 *   onFocus={handleFocus}
 *   onBlur={(e) => handleBlur(e, '0')}
 * />
 */

export function useAutoClearInput(defaultValue = '0', onValueChange = () => {}) {
  /**
   * Handle focus event - clears the default value
   * When user focuses on field showing the default value, clear it to allow input
   */
  const handleFocus = (event) => {
    if (event.target.value === String(defaultValue)) {
      // Clear the field to show it's ready for input
      event.target.value = ''
    }
  }

  /**
   * Handle blur event - restores default value if empty
   * When user leaves the field without entering a value, restore the default
   */
  const handleBlur = (event, resetValue = defaultValue) => {
    if (event.target.value === '' || event.target.value.trim() === '') {
      // Restore default value and update state
      onValueChange(resetValue)
    }
  }

  /**
   * Handle change event - update the value
   * Standard change handler for the input field
   */
  const handleChange = (event) => {
    const newValue = event.target.value
    onValueChange(newValue)
  }

  return {
    handleFocus,
    handleBlur,
    handleChange
  }
}

/**
 * Alternative: Direct event handlers for simpler use cases
 *
 * Use these if you prefer to inline the logic without a hook:
 *
 * const handleQuantityFocus = (event) => {
 *   if (event.target.value === '0') {
 *     event.target.value = ''
 *   }
 * }
 *
 * const handleQuantityBlur = (event, productId) => {
 *   if (event.target.value === '' || event.target.value.trim() === '') {
 *     updateValue(productId, '0')
 *   }
 * }
 */
